import type { DataSchema, RedirectType, UpdateAgendaRecordRes } from '../../types';
import { TABLE_CELL_TYPE } from '../../types/enums.ts';

export const BUTTONS_GROUP_ATTRIBUTE_KEY = 'actions';

export const convertObjectToArray = <V extends object>(obj: Record<string, V>): Array<V & { id: string }> => {
  return Object.entries(obj).map(([id, value]) => ({
    id,
    ...value,
  }));
};

export const groupDataSchemaButtons = <T extends DataSchema>(data: T, customKey: string, label: string): DataSchema => {
  const entries = Object.entries(data) as [keyof T, T[keyof T]][];
  const entriesWithoutButtons = entries.filter(([, item]) => item.type !== TABLE_CELL_TYPE.HELIOS_BUTTON);

  const restMap = Object.fromEntries(entriesWithoutButtons) as DataSchema;

  const grouped: DataSchema[keyof DataSchema] = {
    label,
    formItemDefinition: {
      type: 'Button',
    },
    type: TABLE_CELL_TYPE.HELIOS_BUTTON,
    originalType: TABLE_CELL_TYPE.BUTTON,
    customKey,
    group: '',
    groupName: '',
    groupOrder: 0,
    isEditable: false,
  };

  return {
    ...restMap,
    [BUTTONS_GROUP_ATTRIBUTE_KEY]: grouped,
  };
};

export type ProcessKeys = {
  keys: string[];
  schema: DataSchema;
  buttonsGroup?: (DataSchema & { id: string })[];
};

export interface ProcessKeysProps {
  sortedKeys: string[];
  dataSchema: DataSchema;
  buttonsGroupCustomKey?: string;
  isNested?: boolean;
}

export const processKeys = ({
  sortedKeys,
  dataSchema,
  buttonsGroupCustomKey = BUTTONS_GROUP_ATTRIBUTE_KEY,
  isNested,
}: ProcessKeysProps): ProcessKeys => {
  if (!isNested) {
    return { keys: sortedKeys, schema: dataSchema };
  }

  const buttonsGroup = convertObjectToArray(dataSchema)?.filter((item) => item.type === TABLE_CELL_TYPE.HELIOS_BUTTON) as unknown as (DataSchema & {
    id: string;
  })[];
  const modifiedDataSchema = groupDataSchemaButtons(dataSchema, buttonsGroupCustomKey, BUTTONS_GROUP_ATTRIBUTE_KEY);
  const modifiedSortedKeys = [...sortedKeys.filter((id) => !buttonsGroup?.map((item) => item.id).includes(id)), buttonsGroupCustomKey];

  return { keys: modifiedSortedKeys, schema: modifiedDataSchema, buttonsGroup: buttonsGroup };
};

export const getAgendaRecordPath = (
  type: RedirectType,
  moduleId?: string,
  systemType?: string,
  agendaId?: string,
  recordId?: string,
  childId?: string
): string => {
  const basicUrl = `/user/modules/${moduleId}/${childId ? `m/${childId}/` : ''}${systemType}/${agendaId}`;

  switch (type) {
    case 'detail':
      return `${basicUrl}/r/${recordId}/detail`;
    case 'edit':
      return `${basicUrl}/r/${recordId}`;
    case 'close':
    default:
      return basicUrl;
  }
};

export const getRedirectTarget = (redirectAction: UpdateAgendaRecordRes['redirectAction']) => {
  const { otherModuleId, otherAgendaId, otherViewId, otherRecordId } = redirectAction ?? {};

  if (!otherModuleId || !otherAgendaId) {
    return null;
  }

  if (otherRecordId && otherModuleId && otherAgendaId) {
    return `/user/modules/${otherModuleId}/Flow/${otherAgendaId}/r/${otherRecordId}/detail`;
  }

  if (otherViewId) {
    return `/user/modules/${otherModuleId}/Flow/${otherAgendaId}/view/${otherViewId}`;
  }
  return `/user/modules/${otherModuleId}/Flow/${otherAgendaId}/r/new`;
};

export const isRecordDeleted = (recordId: string, deleteRecordAction: UpdateAgendaRecordRes['deleteRecordAction']) => {
  return deleteRecordAction?.deletedRecords?.includes(recordId) ?? false;
};
